'use client';

import {
  DEFAULT_MAX_PARALLEL_LOGINS,
  MAX_PARALLEL_LOGINS_LIMIT,
} from '@terra/shared-contracts-applications';
import {
  CREDENTIAL_LOGIN_LEASE_ENFORCEMENT_ENABLED_FLAG,
  NEO_RUN_LOGIN_ENABLED_FLAG,
} from '@terra/shared-contracts-feature-flags';
import {
  LoginBehaviorConfiguration,
  toasterApi,
  toLoginBehaviorConfigurationValue,
  type LoginBehaviorConfigurationChange,
  type LoginBehaviorConfigurationStatus,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { useApplicationById } from '../../hooks/api/useApplicationById';
import { useDetectedLoginBehavior } from '../../hooks/api/useDetectedLoginBehavior';
import { useUpdateApplicationFields } from '../../hooks/api/useUpdateApplicationFields';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

type LoginBehaviorCardProps = {
  applicationId: string;
  isReadOnly?: boolean;
};

export function LoginBehaviorCard({
  applicationId,
  isReadOnly = false,
}: LoginBehaviorCardProps) {
  const tSettings = useTranslations('settings');
  const { isFeatureFlagEnabled: isRunLoginEnabled } = useFeatureFlags(
    NEO_RUN_LOGIN_ENABLED_FLAG,
  );
  const { isFeatureFlagEnabled: isLeaseEnforcementEnabled } = useFeatureFlags(
    CREDENTIAL_LOGIN_LEASE_ENFORCEMENT_ENABLED_FLAG,
  );
  const isVisible = isRunLoginEnabled || isLeaseEnforcementEnabled;
  const { application } = useApplicationById({
    id: applicationId,
    enabled: isVisible,
  });
  const { detected } = useDetectedLoginBehavior({
    applicationId,
    enabled: isVisible,
  });
  const { mutate, isPending, isError } = useUpdateApplicationFields();
  const status: LoginBehaviorConfigurationStatus = isPending
    ? 'saving'
    : isError
      ? 'error'
      : 'idle';

  const handleChange = useCallback(
    (loginBehavior: LoginBehaviorConfigurationChange) => {
      if (isReadOnly) {
        return;
      }

      mutate(
        { applicationId, body: { loginBehavior } },
        {
          onSuccess: () => {
            toasterApi.success({
              title: tSettings('settingsSaved'),
              description: tSettings('settingsSavedDescription'),
            });
          },
          onError: () => {
            toasterApi.error({
              title: tSettings('saveFailed'),
              description: tSettings('saveFailedDescription'),
            });
          },
        },
      );
    },
    [applicationId, isReadOnly, mutate, tSettings],
  );

  if (!isVisible || !application) {
    return null;
  }

  return (
    <LoginBehaviorConfiguration
      value={toLoginBehaviorConfigurationValue(application.loginBehavior)}
      onChange={handleChange}
      parallelExecutionEnabled={application.supportsParallelExecution ?? true}
      maxParallelLoginsLimit={MAX_PARALLEL_LOGINS_LIMIT}
      defaultMaxParallelLogins={DEFAULT_MAX_PARALLEL_LOGINS}
      detected={detected}
      disabled={isReadOnly || isPending}
      status={status}
    />
  );
}
